import { useRef, useState } from "react";
import moment from "moment";
import { useIntl } from "react-intl";

import { Dialog } from "@certa/catalyst/components/Dialog";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { Button } from "@certa/catalyst/components/Button";
import { Form, type FormRef } from "@certa/catalyst/components/Form";
import { showToast, ToastTypes } from "@certa/catalyst/components/Toast";

import {
  useUpdateMassDelegation,
  type Delegatee,
  type DelegationRequest
} from "@certa/queries/queries/massDelegation/massDelegation.query";

import {
  ConfirmDelegation,
  type SubmittedDelegation
} from "../ConfirmDelegation";
import { DelegateTasksFormFields } from "./DelegateTasksFormFields";

import styles from "./DelegateTasks.module.css";

export type TransformedDelegationRequest = Omit<
  DelegationRequest,
  "user" | "creator"
> & {
  delegate: string;
};

type DelegateTasksProps = {
  canShow: boolean;
  onClose: () => void;
  selectedDelegationRequest?: TransformedDelegationRequest;
  onCreationSuccess: () => void;
};

export const DelegateTasks = (props: DelegateTasksProps) => {
  const { canShow, onClose, selectedDelegationRequest, onCreationSuccess } =
    props;

  const intl = useIntl();

  const formRef = useRef<FormRef>(null);

  const [submittedDelegation, setSubmittedDelegation] =
    useState<SubmittedDelegation>();
  const [selectedDelegate, setSelectedDelegate] =
    useState<Delegatee["results"][number]>();

  const onUpdateDelegate = (value?: Delegatee["results"][number]) => {
    setSelectedDelegate(value);
  };

  const [canShowConfirmDelegation, setCanShowConfirmDelegation] =
    useState(false);

  const initialValues = getInitialvalues(
    selectedDelegationRequest,
    submittedDelegation,
    selectedDelegate
  );

  const {
    mutate: updateMassDelegationMutation,
    isLoading: isUpdatingDelegation
  } = useUpdateMassDelegation({
    onSuccess: () => {
      showToast({
        title: intl.formatMessage({
          id: "delegations.delegationUpdatedSuccessfully",
          defaultMessage: "Delegation updated successfully"
        }),
        type: ToastTypes.SUCCESS
      });
      onClose();
    },
    onError: error => {
      showToast({
        title: intl.formatMessage({
          id: "delegations.failedToUpdateDelegation",
          defaultMessage: "Failed to update delegation"
        }),
        type: ToastTypes.ERROR
      });
      console.error(error);
    }
  });

  const updateDelegation = (value: SubmittedDelegation) => {
    const payload = {
      delegation_id: value?.id,
      name: value.delegationName,
      delegate_future_tasks: true,
      delegate_in_progress_tasks: true,
      start_date: moment(value.startDate).format("YYYY-MM-DD"),
      end_date: moment(value.endDate).format("YYYY-MM-DD"),
      user_email: value.selectDelegate,
      reason: value.reasonNotes
    };

    updateMassDelegationMutation(payload);
  };

  const handleSubmit = (value: SubmittedDelegation) => {
    const updatedDelegation = { ...value, id: selectedDelegationRequest?.id };
    setSubmittedDelegation(updatedDelegation);
    if (selectedDelegationRequest?.delegate !== value?.selectDelegate) {
      setCanShowConfirmDelegation(true);
    } else if (selectedDelegationRequest?.id) {
      updateDelegation(updatedDelegation);
    }
  };

  if (selectedDelegate && canShowConfirmDelegation && submittedDelegation) {
    return (
      <ConfirmDelegation
        delegatedUser={selectedDelegate}
        submittedDelegation={submittedDelegation}
        canShow={!!submittedDelegation}
        onClose={onClose}
        onUpdateDelegation={updateDelegation}
        isUpdatingDelegation={isUpdatingDelegation}
        onCancel={() => {
          setCanShowConfirmDelegation(false);
        }}
        onCreationSuccess={onCreationSuccess}
      />
    );
  }

  const title = intl.formatMessage({
    id: "delegations.title",
    defaultMessage: "Delegate Tasks"
  });

  const description = intl.formatMessage({
    id: "delegations.description",
    defaultMessage:
      "Delegates will see all tasks you've delegated to them, and can complete them if they have access. Delegated tasks are only visible to the delegate during the delegation period."
  });

  const backButtonLabel = intl.formatMessage({
    id: "commonTextInstances.back",
    defaultMessage: "Back"
  });

  const delegateButtonLabel = intl.formatMessage({
    id: "delegations.delegate",
    defaultMessage: "Delegate"
  });

  return (
    <Dialog
      show={canShow}
      width="40%"
      onClose={onClose}
      showCloseIcon={!isUpdatingDelegation}
      preventCloseOnClickOutside={isUpdatingDelegation}
      title={title}
      description={description}
    >
      <Form
        ref={formRef}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        className={styles.delegateTasksForm}
      >
        <Stack gap="16" justify="space-between" width="100%">
          <DelegateTasksFormFields
            isDisabled={isUpdatingDelegation}
            onUpdateDelegate={onUpdateDelegate}
            selectedDelegationRequest={selectedDelegationRequest}
          />
          <Stack
            direction="horizontal"
            gap="12"
            width="100%"
            justify="flex-end"
          >
            <Button variant="Outline" onClick={onClose}>
              {backButtonLabel}
            </Button>
            <Button
              htmlType="submit"
              variant="Filled"
              disabled={isUpdatingDelegation}
              loading={isUpdatingDelegation}
            >
              {delegateButtonLabel}
            </Button>
          </Stack>
        </Stack>
      </Form>
    </Dialog>
  );
};

function getInitialvalues(
  selectedDelegationRequest?: TransformedDelegationRequest,
  submittedDelegation?: SubmittedDelegation,
  selectedDelegate?: Delegatee["results"][number]
) {
  const initialDelegationName =
    selectedDelegationRequest?.name ||
    submittedDelegation?.delegationName ||
    "";

  const initialSelectDelegate =
    selectedDelegate?.email ||
    submittedDelegation?.selectDelegate ||
    selectedDelegationRequest?.delegate ||
    "";

  const initialStartDate =
    submittedDelegation?.startDate ||
    selectedDelegationRequest?.startDate ||
    "";

  const initialEndDate =
    submittedDelegation?.endDate || selectedDelegationRequest?.endDate || "";

  const initialReasonNotes =
    submittedDelegation?.reasonNotes || selectedDelegationRequest?.reason || "";

  const initialValues = {
    delegationName: initialDelegationName,
    selectDelegate: initialSelectDelegate,
    startDate: initialStartDate ? moment(initialStartDate) : "",
    endDate: initialEndDate ? moment(initialEndDate) : "",
    reasonNotes: initialReasonNotes || ""
  };

  return initialValues;
}
